/* Uses an independent Storefront cart, with a verified Ajax-cart checkout fallback. */
(() => {
  const backupKey = 'bogart_saved_furniture_cart_v1';
  const checkoutKey = 'bogart_sample_checkout_session_v1';

  function originDomain() {
    return window.location.hostname.toLowerCase().replace(/^www\./, '');
  }

  async function request(path, body) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}${path}`, body === undefined
      ? { cache: 'no-store', headers: { Accept: 'application/json' } }
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || window.themeVariables.sampleCheckout.error);
    return result;
  }

  function cartItems(cart) {
    return cart.items.map((item) => ({
      id: item.variant_id || item.id,
      quantity: item.quantity,
      properties: item.properties || {},
      ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}),
    }));
  }

  function saveBackup(cart) {
    const backup = {
      items: cartItems(cart),
      note: cart.note || '',
      attributes: cart.attributes || {},
      discounts: (cart.discount_codes || []).map((discount) => discount.code),
      phase: 'checkout',
    };
    const serialized = JSON.stringify(backup);
    localStorage.setItem(backupKey, serialized);
    if (localStorage.getItem(backupKey) !== serialized) throw new Error(window.themeVariables.sampleCheckout.error);
  }

  function saveCheckoutSession(ids) {
    const serialized = JSON.stringify({ ids, startedAt: Date.now() });
    localStorage.setItem(checkoutKey, serialized);
    if (localStorage.getItem(checkoutKey) !== serialized) throw new Error(window.themeVariables.sampleCheckout.error);
  }

  async function createAjaxCheckout(ids) {
    const cart = await request('cart.js');
    let classified = { regularQuantity: cart.items.length };
    if (cart.items.length && window.ATFabricSampleCart) {
      try {
        classified = window.ATFabricSampleCart.inspect(await window.ATFabricSampleCart.getCart());
      } catch {
        // Treat unknown existing lines as regular products so they are backed up.
      }
    }

    if (classified.regularQuantity > 0) {
      if (localStorage.getItem(backupKey)) throw new Error(window.themeVariables.sampleCheckout.error);
      saveBackup(cart);
    }

    if (cart.items.length) await request('cart/clear.js', {});
    if (cart.note || Object.keys(cart.attributes || {}).length || (cart.discount_codes || []).length) {
      await request('cart/update.js', {
        note: '',
        attributes: Object.fromEntries(Object.keys(cart.attributes || {}).map((key) => [key, ''])),
        discount: '',
      });
    }
    await request('cart/add.js', { items: ids.map((id) => ({ id: Number(id), quantity: 1 })) });
    await request('cart/update.js', { attributes: { checkout_origin_domain: originDomain() } });

    const addedCart = await request('cart.js');
    const quantities = new Map(addedCart.items.map((item) => [String(item.variant_id || item.id), item.quantity]));
    if (ids.some((id) => quantities.get(id) !== 1)) {
      throw new Error(window.themeVariables.sampleCheckout.error);
    }
    saveCheckoutSession(ids);
    return `${window.Shopify?.routes?.root || '/'}checkout`;
  }

  async function createStorefrontCheckout(ids) {
    const config = window.themeVariables.sampleCheckout;
    const merchandiseIds = ids.map((id) => `gid://shopify/ProductVariant/${id}`);
    const response = await fetch('/api/2026-07/graphql.json', {
      method: 'POST', credentials: 'omit',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: `mutation SampleCheckout($input: CartInput!, $country: CountryCode!, $language: LanguageCode!)
          @inContext(country: $country, language: $language) {
          cartCreate(input: $input) {
            cart { checkoutUrl totalQuantity lines(first: 7) { nodes { quantity merchandise { ... on ProductVariant { id } } } } }
            userErrors { message } warnings { message }
          }
        }`,
        variables: {
          country: config.country, language: config.language,
          input: {
            attributes: [{ key: 'checkout_origin_domain', value: originDomain() }],
            buyerIdentity: { countryCode: config.country },
            lines: merchandiseIds.map((merchandiseId) => ({ merchandiseId, quantity: 1 })),
          },
        },
      }),
    });
    const result = await response.json();
    const payload = result.data?.cartCreate;
    if (!response.ok || result.errors?.length || !payload || payload.userErrors?.length || payload.warnings?.length) {
      throw new Error(config.error);
    }
    const cart = payload.cart;
    const lines = cart?.lines?.nodes || [];
    if (!cart?.checkoutUrl || cart.totalQuantity !== ids.length || lines.length !== ids.length
      || merchandiseIds.some((id) => !lines.some((line) => line.merchandise?.id === id && line.quantity === 1))) {
      throw new Error(config.error);
    }
    return cart.checkoutUrl;
  }

  async function create(variantIds) {
    const config = window.themeVariables.sampleCheckout;
    const ids = variantIds.map(String);
    if (!ids.length || ids.length > 7 || new Set(ids).size !== ids.length || ids.some((id) => !/^[1-9]\d*$/.test(id))) {
      throw new Error(config.error);
    }

    try {
      return await createStorefrontCheckout(ids);
    } catch {
      return createAjaxCheckout(ids);
    }
  }
  window.ATSampleCheckout = { create };
})();
